'use client';

import { useQuery } from '@tanstack/react-query';
import Axios from '@/lib/axiosConfig';
import { useAuth } from '@/context/Auth';
import { useIsPublic } from '@/context/PublicMode';

/**
 * IDs of the posts the current user is *currently* promoting.
 *
 * Why a separate request rather than an `isPromoted` flag on each post: the
 * Promote button only renders on a business user's own posts, so enriching
 * every row of a 100-post feed would do ~100 lookups to answer a question that
 * applies to a handful of them. One call for the user's own promotions is far
 * cheaper and gives the same answer.
 *
 * It is also the only thing that can answer it. Promoting redirects to Stripe
 * checkout, so the user leaves the app and comes back on a fresh page load —
 * there is no in-session success state to remember.
 */

/**
 * A promotion record. Note `id` is the promotion's own id — the post it points
 * at is `postID`. Reading `id` here would silently never match a post.
 */
type Promotion = {
  id: string;
  postID: string;
  startsAt: string;
  endsAt: string;
};

type PromotedPostsResponse = {
  // A flat array, not the { rows, count } envelope the list endpoints use.
  responseObject: Promotion[] | null;
  message: string | null;
  success: boolean;
};

export const usePromotedPostIds = (options?: { enabled?: boolean }) => {
  const { getAPIRequestTokenHeaders, idTokenPayload } = useAuth();
  const isPublic = useIsPublic();
  const roleIsBusiness = idTokenPayload?.['custom:role'] === '1';

  return useQuery<Set<string>>({
    queryKey: ['posts', 'promoted', 'ids'],
    queryFn: async () => {
      const res = await Axios.get<PromotedPostsResponse>('/posts/promoted', {
        headers: getAPIRequestTokenHeaders(),
      });

      const now = Date.now();
      const active = new Set<string>();

      for (const promotion of res.data?.responseObject ?? []) {
        if (!promotion?.postID) continue;
        // Expired promotions stay in this list. Keeping them would hide the
        // button forever, so a business could never re-promote a post after its
        // run ended. An unparseable endsAt counts as active — better to hide a
        // button we should have shown than to offer one the API will reject.
        const endsAt = Date.parse(promotion.endsAt);
        if (Number.isNaN(endsAt) || endsAt > now) {
          active.add(promotion.postID);
        }
      }

      return active;
    },
    // Only business users can promote, so nobody else pays for this request.
    enabled: options?.enabled ?? (!isPublic && roleIsBusiness),
    staleTime: 5 * 60 * 1000,
  });
};
